/**
 * MCP tools: the family library surface — import IFC content as static-mesh
 * families, browse the live family registry, and place a loaded family at
 * exact coordinates. This mirrors the drag & drop UX for agents: they cannot
 * drag files, but IFC is a text (STEP) format, so the file content itself can
 * travel through the bridge as a string argument.
 */

import { familyManager } from '../../core/FamilyManager';
import { importIfcFile } from '../ifc/IfcFamilyImporter';
import { buildStaticFamilyElement } from '../../tools/PlaceStaticFamilyTool';
import { useBIMStore } from '../../state/useBIMStore';
import type { McpToolHandler } from './mcpResultUtils';
import { asNumber, asString, asVec3, fail, okJson, okText } from './mcpResultUtils';

/** Import IFC STEP text into the family library (one static family per product). */
export const handleImportIfc: McpToolHandler = async (args) => {
    const content = asString(args.content);
    if (!content) {
        return fail("Missing required argument 'content' — the full IFC file text (STEP format).");
    }
    if (!content.slice(0, 512).toUpperCase().includes('ISO-10303-21')) {
        return fail('Content does not look like an IFC file: missing the ISO-10303-21 header.');
    }
    const fileName = asString(args.fileName) ?? 'agent-import.ifc';

    try {
        const bytes = new TextEncoder().encode(content);
        const summary = await importIfcFile(fileName, bytes);
        if (summary.productCount === 0) {
            return fail('The IFC file opened but contains no tessellatable products.');
        }
        return okJson(
            summary,
            `Imported ${summary.productCount} famil${summary.productCount === 1 ? 'y' : 'ies'} from "${fileName}": ${summary.families.join(', ')}. Place them with yap_place_family.`,
        );
    } catch (error) {
        return fail(`IFC import failed: ${error instanceof Error ? error.message : String(error)}`);
    }
};

/** List registered families with their types and static-asset state. */
export const handleListFamilies: McpToolHandler = (args) => {
    const category = asString(args.category);
    const nameContains = asString(args.nameContains)?.toLowerCase();
    const limit = Math.min(Math.max(asNumber(args.limit) ?? 200, 1), 2000);

    let families = familyManager.getAllFamilies(category ?? undefined);
    if (nameContains) {
        families = families.filter((f) => f.name.toLowerCase().includes(nameContains));
    }
    const truncated = families.length > limit;
    const rows = families.slice(0, limit).map((f) => ({
        name: f.name,
        category: f.category,
        static: !!f.geometrySchema?.staticAsset,
        assetSource: f.geometrySchema?.staticAsset?.source,
        types: f.symbols.map((s) => s.name),
    }));
    return okJson(
        { total: families.length, returned: rows.length, truncated, families: rows },
        `${families.length} famil${families.length === 1 ? 'y' : 'ies'} registered.`,
    );
};

/** Place a loaded static (IFC) family at an exact position, undoable. */
export const handlePlaceFamily: McpToolHandler = (args) => {
    const familyName = asString(args.family);
    if (!familyName) return fail("Missing required argument 'family' (exact name from yap_list_families).");
    const position = asVec3(args.position);
    if (!position) return fail("Missing or malformed 'position' — expected [x, y, z] in metres.");

    // Accept the name case-insensitively and tolerate the "… (IfcType)" suffix.
    let resolved = familyName;
    if (!familyManager.hasFamily(resolved)) {
        const hit = familyManager.getAllFamilies().find(
            (f) => f.name.toLowerCase() === familyName.toLowerCase()
        );
        if (!hit) {
            return fail(`Family '${familyName}' is not loaded. Import it first with yap_import_ifc or list names with yap_list_families.`);
        }
        resolved = hit.name;
    }

    const rotation = asNumber(args.rotation) ?? 0;
    const element = buildStaticFamilyElement(resolved, position, rotation);
    if (!element) {
        return fail(`Family '${resolved}' has no static mesh to place (parametric families are placed with the interactive tools).`);
    }
    useBIMStore.getState().addElement(element);
    return okText(
        `Placed "${resolved}" at (${position.map((v) => v.toFixed(2)).join(', ')}) · rotation ${rotation}°. Element id: ${element.id}`,
    );
};
